/**
 * Live Activity Service
 *
 * Manages iOS Live Activities for the Luma focus timer.
 * Live Activities appear automatically on the lock screen and Dynamic Island
 * when a timer session is active.
 */

import { Capacitor } from '@capacitor/core';
import { LumaWidgetPlugin } from '@/lib/widgetPlugin';

// MARK: - Service Class

export class LiveActivityService {
  private isActive = false;
  private listeners: Map<string, (() => void)[]> = new Map();

  constructor() {
    this.setupListeners();
  }

  /**
   * Set up listeners for Live Activity button taps
   */
  private setupListeners() {
    if (!LumaWidgetPlugin?.addListener) return;
    LumaWidgetPlugin.addListener('liveActivityPauseTapped', () => {
      console.log('Live Activity: Pause button tapped');
      this.notifyListeners('pause');
    });
    LumaWidgetPlugin.addListener('liveActivityCancelTapped', () => {
      console.log('Live Activity: Cancel button tapped');
      this.notifyListeners('cancel');
    });
  }

  /**
   * Check if Live Activities are available on this device
   */
  async isSupported(): Promise<boolean> {
    if (!LumaWidgetPlugin?.isLiveActivitySupported || Capacitor.getPlatform() !== 'ios') {
      return false;
    }
    try {
      const { supported, enabled } = await LumaWidgetPlugin.isLiveActivitySupported();
      return supported && enabled;
    } catch (error) {
      console.error('Error checking Live Activity support:', error);
      return false;
    }
  }

  /**
   * Start a Live Activity for a timer session
   */
  async startTimer(params: {
    taskName: string;
    sessionDuration: number;
    timeToNextMilestone: number;
    currentMilestone: number;
    totalMilestones: number;
    motivation: string;
    timerMode: 'maker' | 'manager';
    isPaused?: boolean;
  }): Promise<boolean> {
    try {
      // Check if supported first
      const supported = await this.isSupported();
      if (!supported) {
        console.log('Live Activities not supported or not enabled');
        return false;
      }

      const result = await LumaWidgetPlugin!.startLiveActivity!({
        taskName: params.taskName,
        sessionDuration: params.sessionDuration,
        timeToNextMilestone: params.timeToNextMilestone,
        currentMilestone: params.currentMilestone,
        totalMilestones: params.totalMilestones,
        motivation: params.motivation,
        timerMode: params.timerMode,
        isPaused: params.isPaused ?? false,
        timerState: params.isPaused ? 'paused' : 'running',
      });

      this.isActive = result.success;
      console.log('Live Activity started:', result.activityId);
      return result.success;
    } catch (error) {
      console.error('Failed to start Live Activity:', error);
      return false;
    }
  }

  /**
   * Update the Live Activity (e.g., when pausing, resuming, or changing milestone)
   */
  async updateTimer(params: {
    timeToNextMilestone?: number;
    currentMilestone?: number;
    totalMilestones?: number;
    motivation?: string;
    isPaused?: boolean;
  }): Promise<boolean> {
    if (!this.isActive) {
      console.log('No active Live Activity to update');
      return false;
    }

    try {
      const result = await LumaWidgetPlugin!.updateLiveActivity!({
        timeToNextMilestone: params.timeToNextMilestone,
        currentMilestone: params.currentMilestone,
        totalMilestones: params.totalMilestones,
        motivation: params.motivation,
        isPaused: params.isPaused,
        timerState: params.isPaused ? 'paused' : 'running',
      });

      console.log('Live Activity updated');
      return result.success;
    } catch (error) {
      console.error('Failed to update Live Activity:', error);
      return false;
    }
  }

  /**
   * End the Live Activity (removes it from lock screen)
   */
  async endTimer(): Promise<boolean> {
    if (!this.isActive) {
      console.log('No active Live Activity to end');
      return false;
    }

    try {
      const result = await LumaWidgetPlugin!.endLiveActivity!();
      this.isActive = false;
      console.log('Live Activity ended');
      return result.success;
    } catch (error) {
      console.error('Failed to end Live Activity:', error);
      return false;
    }
  }

  /**
   * Register a callback for when Live Activity buttons are tapped
   */
  on(event: 'pause' | 'cancel', callback: () => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(callback);
  }

  /**
   * Unregister a callback
   */
  off(event: 'pause' | 'cancel', callback: () => void) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      const index = eventListeners.indexOf(callback);
      if (index > -1) {
        eventListeners.splice(index, 1);
      }
    }
  }

  /**
   * Notify all registered listeners for an event
   */
  private notifyListeners(event: 'pause' | 'cancel') {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => callback());
    }
  }

  /**
   * Remove all listeners
   */
  removeAllListeners() {
    this.listeners.clear();
    LumaWidgetPlugin?.removeAllListeners?.();
  }

  /**
   * Check if a Live Activity is currently active
   */
  get active(): boolean {
    return this.isActive;
  }
}

// MARK: - Singleton Export

/**
 * Singleton instance of the Live Activity service
 */
export const liveActivityService = new LiveActivityService();
